import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { acknowledgeNotice, getPublicNotice, getPublicNoticeByCode } from "../../digi/NewMasterSheet/ResidentDirectory/noticesApi";
import "./noticeView.scss";

const formatBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};
const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-CA", { day: "numeric", month: "long", year: "numeric" });
};
const isPdf = (a) => /pdf/i.test(a.mime || "") || /\.pdf$/i.test(a.filename || "");
const isImage = (a) => /^image\//i.test(a.mime || "") || /\.(png|jpe?g|webp)$/i.test(a.filename || "");
const isVideo = (a) => /^video\//i.test(a.mime || "") || /\.(mp4|m4v|mov)$/i.test(a.filename || "");
/** Web copies and posters belong to another file; they are never listed on their own. */
const isDerivative = (a) => a.kind === "inline" || a.kind === "poster";

/**
 * What a resident sees from the link in their email: the notice, its message, and the file,
 * readable on a phone without logging in. The token in the address is the authentication.
 */
const NoticeView = () => {
  const { id, token, code } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [ack, setAck] = useState({ at: null, busy: false, error: "" });

  useEffect(() => {
    let active = true;
    // Two ways in: the short code from a text message, or the original long link that is
    // still sitting in inboxes from before short links existed. Both land here.
    const load = code ? getPublicNoticeByCode(code) : getPublicNotice(id, token);
    load
      .then((res) => {
        if (!active) return;
        if (!res?.success) throw new Error(res?.message || "This notice could not be loaded.");
        setState({ loading: false, error: "", data: res.data });
      })
      .catch((err) => {
        if (!active) return;
        setState({ loading: false, error: err?.response?.data?.message || err.message || "This notice could not be loaded.", data: null });
      });
    return () => {
      active = false;
    };
  }, [id, token, code]);

  // A notice that asks to be acknowledged remembers the answer, so a resident coming back to
  // the link sees what they already pressed rather than being asked twice.
  useEffect(() => {
    if (state.data?.acknowledged_at) setAck((a) => ({ ...a, at: state.data.acknowledged_at }));
  }, [state.data]);

  const confirmRead = async () => {
    setAck((a) => ({ ...a, busy: true, error: "" }));
    try {
      const res = await acknowledgeNotice(code);
      setAck({ at: res?.data?.acknowledged_at || new Date().toISOString(), busy: false, error: "" });
    } catch (err) {
      setAck((a) => ({ ...a, busy: false, error: "That did not go through. Please try again." }));
    }
  };

  const n = state.data;
  const shown = (n?.attachments || []).filter((a) => !isDerivative(a));
  const posters = (n?.attachments || []).filter((a) => a.kind === "poster");
  const posterFor = (attachmentId) => posters.find((p) => p.poster_for === attachmentId)?.url || undefined;
  const videos = shown.filter(isVideo);
  const main = shown.find((a) => !isVideo(a));
  const rest = shown.filter((a) => a !== main && !isVideo(a));

  return (
    <div className="nv-page">
      <div className="nv-card">
        {state.loading ? (
          <div className="nv-state">Loading your notice…</div>
        ) : state.error ? (
          <div className="nv-state">
            <span className="nv-state-ic">
              <FeatherIcon icon="alert-circle" size={22} />
            </span>
            <h1>This link is not available</h1>
            <p>{state.error}</p>
            <p className="nv-muted">Links in notice emails work for 60 days. If you need this notice, reply to the email and the property will send it again.</p>
          </div>
        ) : (
          <>
            <header className="nv-head">
              <p className="nv-eyebrow">{n.type} notice{n.company_name ? ` from ${n.company_name}` : ""}</p>
              <h1>{n.title}</h1>
              <p className="nv-meta">
                {[n.unit_no ? `Unit ${n.unit_no}` : "", n.property_name, n.building_name].filter(Boolean).join(" · ")}
                {n.sent_at ? ` · ${formatDate(n.sent_at)}` : ""}
              </p>
            </header>

            {n.resident_name ? <p className="nv-hi">Hi {n.resident_name},</p> : null}
            {n.message ? <div className="nv-message">{n.message}</div> : null}

            {main ? (
              <section className="nv-doc">
                <div className="nv-doc-head">
                  <span className="nv-doc-name">
                    <FeatherIcon icon="file-text" size={15} /> {main.filename}
                    <small>{formatBytes(main.size)}</small>
                  </span>
                  <a className="nv-btn" href={main.url} download={main.filename} target="_blank" rel="noopener noreferrer">
                    <FeatherIcon icon="download" size={14} /> Download
                  </a>
                </div>
                {isPdf(main) ? (
                  <iframe className="nv-frame" src={main.url} title={main.filename} />
                ) : isImage(main) ? (
                  <img className="nv-image" src={main.url} alt={main.filename} />
                ) : (
                  <p className="nv-muted">Use Download to open this file.</p>
                )}
              </section>
            ) : null}

            {videos.map((v) => (
              <section className="nv-video" key={v.attachment_id}>
                <video
                  className="nv-player"
                  controls
                  preload="metadata"
                  playsInline
                  poster={posterFor(v.attachment_id)}
                  onError={(e) => e.currentTarget.classList.add("is-broken")}
                >
                  <source src={v.url} type={v.mime || "video/mp4"} />
                </video>
                <a className="nv-btn nv-video-dl" href={v.url} download={v.filename} target="_blank" rel="noopener noreferrer">
                  <FeatherIcon icon="download" size={14} /> Download video
                </a>
              </section>
            ))}

            {rest.length > 0 && (
              <section className="nv-extras">
                <p className="nv-eyebrow">Also attached</p>
                {rest.map((a) => (
                  <a key={a.attachment_id} className="nv-extra" href={a.url} download={a.filename} target="_blank" rel="noopener noreferrer">
                    <FeatherIcon icon="paperclip" size={14} /> {a.filename} <small>{formatBytes(a.size)}</small>
                  </a>
                ))}
              </section>
            )}

            {n.require_acknowledgement && code ? (
              <section className="nv-ack">
                {ack.at ? (
                  <p className="nv-ack-done">
                    <FeatherIcon icon="check-circle" size={16} /> Acknowledged on {formatDate(ack.at)}
                  </p>
                ) : (
                  <>
                    <button type="button" className="nv-ack-btn" onClick={confirmRead} disabled={ack.busy}>
                      {ack.busy ? "Sending…" : "Got it — I've read this"}
                    </button>
                    {ack.error ? <p className="nv-ack-err">{ack.error}</p> : null}
                  </>
                )}
              </section>
            ) : null}

            <footer className="nv-foot">
              {n.sender_name ? <p>Sent by {n.sender_name}{n.company_name ? `, ${n.company_name}` : ""}. Reply to the email to reach them.</p> : null}
              <p className="nv-muted">This page is personal to you. Please do not share the link.</p>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export { NoticeView };
export default NoticeView;
